/**
 * Couche unique « DocumentTheme » : logo, couleurs, police, en-tête et pied de page des documents du
 * CLIENT (bulletin, attestations, solde de tout compte, factures, rapports…).
 *
 * - Sans charte enregistrée (`tenant.branding` absent) : sortie strictement identique à l'existant
 *   (logo Nexxus, variables du thème Nexxus) — garanti par des instantanés de non-régression.
 * - Avec charte : variables CSS posées sur l'élément racine du document, logo du client en data-URI
 *   (aucune requête réseau), police issue d'une liste fermée. Toute valeur interpolée provient d'un
 *   type validé (hex, clé de police, data-URI reconstruite) ou est échappée.
 * - Les gabarits officiels DTS / DAS 1 n'utilisent PAS le thème client (`nxBannerLeftHtml()` sans thème).
 */
import logoNexxusUrl from '@presentation/assets/logo-nexxus.png';
import type { Tenant } from '@shared/types/Tenant';
import { INACTIVE_THEME, resolveBrandTheme, type BrandTheme } from '@domain/branding/brandTheme';
import { esc } from '@presentation/shell/format';

export type { BrandTheme };

export function nexxusDocumentLogoUrl(): string {
  return logoNexxusUrl;
}

/** Thème du document pour une entreprise (INACTIVE_THEME si aucune charte valide). */
export function documentThemeFor(tenant: Pick<Tenant, 'branding'> | null | undefined): BrandTheme {
  return resolveBrandTheme(tenant?.branding);
}

/** Classe ajoutée à l'élément racine ('' si aucune charte). */
export function brandRootClass(theme: BrandTheme): string {
  return theme.active ? ' nx-branded' : '';
}

/** Déclarations CSS (variables de charte) de l'élément racine ; '' si aucune charte. */
export function brandRootStyleDecls(theme: BrandTheme): string {
  if (!theme.active) return '';
  return Object.entries(theme.cssVars)
    .map(([k, v]) => `${k}:${v}`)
    .join(';');
}

/** Attribut `style` de l'élément racine portant les variables de charte ('' si aucune charte). */
export function brandRootStyleAttr(theme: BrandTheme): string {
  return theme.active ? ` style="${esc(brandRootStyleDecls(theme))}"` : '';
}

/** Bloc <style> de la police du client (à placer DANS l'élément racine) ; '' si police par défaut. */
export function brandFontStyleBlock(theme: BrandTheme): string {
  if (!theme.active || !theme.fontStack) return '';
  // fontStack provient de la liste fermée SAFE_FONTS : aucune saisie libre n'atteint ce CSS.
  return `<style>.nx-branded,.nx-branded *:not(.mono):not([style*="Courier"]){font-family:${theme.fontStack}!important}</style>`;
}

function clientLogoImg(theme: BrandTheme, alt: string, heightPx: number, extra: string): string {
  return `<img class="nx-client-logo" src="${theme.logoDataUri ?? ''}" alt="${esc(alt)}" style="height:${String(heightPx)}px;width:auto;max-width:180px;object-fit:contain;display:block;background:#fff;padding:3px 6px;border-radius:3px;${extra}" />`;
}

/** Bloc gauche bannières nx-* (attestations, DAS, DTS). */
export function nxBannerLeftHtml(
  heightPx = 44,
  theme: BrandTheme = INACTIVE_THEME,
  alt = '',
): string {
  if (theme.logoDataUri && theme.logoPosition === 'left') {
    return `<div class="nx-banner-left">${clientLogoImg(theme, alt, heightPx, '')}</div>`;
  }
  if (theme.logoDataUri) {
    // Logo placé à droite / au centre : la place de gauche reste vide (le nom de l'entreprise est à droite).
    return `<div class="nx-banner-left"></div>`;
  }
  return `<div class="nx-banner-left"><img class="nx-doc-logo" src="${logoNexxusUrl}" alt="Nexxus" style="height:${heightPx}px;width:auto;max-width:120px;object-fit:contain;display:block;" /></div>`;
}

/** Logo du client placé à droite (bannière attestation) ; '' sinon. */
export function nxBannerRightLogoHtml(theme: BrandTheme, alt: string, heightPx = 44): string {
  if (!theme.logoDataUri || theme.logoPosition !== 'right') return '';
  return `<div style="display:flex;justify-content:flex-end;margin-bottom:4px;">${clientLogoImg(theme, alt, heightPx, '')}</div>`;
}

/** Logo du client centré : bandeau pleine largeur au-dessus de l'en-tête ; '' sinon. */
export function brandCenterStripHtml(theme: BrandTheme, alt: string, heightPx = 44): string {
  if (!theme.logoDataUri || theme.logoPosition !== 'center') return '';
  return `<div class="nx-brand-strip" style="background:${theme.primary};padding:8px 16px 0;display:flex;justify-content:center;">${clientLogoImg(theme, alt, heightPx, '')}</div>`;
}

/** Logo pour en-têtes bulletin / facture (fond de la couleur principale). */
export function bulletinHeaderLogoHtml(
  heightPx = 40,
  theme: BrandTheme = INACTIVE_THEME,
  alt = '',
): string {
  if (theme.logoDataUri) {
    if (theme.logoPosition !== 'left') return '';
    return clientLogoImg(theme, alt, heightPx, 'margin-bottom:6px;');
  }
  return `<img class="bulletin-brand-logo" src="${logoNexxusUrl}" alt="Nexxus" style="height:${heightPx}px;width:auto;max-width:110px;object-fit:contain;display:block;margin-bottom:6px;" />`;
}

/** Logo du client à droite dans l'en-tête bulletin / facture ; '' sinon. */
export function bulletinRightLogoHtml(theme: BrandTheme, alt: string, heightPx = 40): string {
  if (!theme.logoDataUri || theme.logoPosition !== 'right') return '';
  return `<div style="display:flex;justify-content:flex-end;margin-bottom:6px;">${clientLogoImg(theme, alt, heightPx, '')}</div>`;
}

/** Ligne d'en-tête libre du client (slogan, agrément…) ; '' si vide ou sans charte. */
export function brandHeaderTextHtml(theme: BrandTheme): string {
  return theme.active && theme.headerText ? `<br>${esc(theme.headerText)}` : '';
}

/** Mentions de pied de page libres du client ; '' si vide ou sans charte. */
export function brandFooterTextHtml(theme: BrandTheme): string {
  return theme.active && theme.footerText ? `<br>${esc(theme.footerText)}` : '';
}
